import { Link } from "react-router-dom";
import { useApi, formatDate, relativeTime, titleCase } from "../lib/hooks.ts";
import type { Summary } from "../lib/types.ts";
import { Badge, Bar, Card, ErrorAlert, Spinner, Stat } from "../components/ui.tsx";

function Bars({ title, data }: { title: string; data: Record<string, number> }) {
  const entries = Object.entries(data).sort((a, b) => b[1] - a[1]);
  const total = entries.reduce((sum, [, value]) => sum + value, 0);
  return (
    <Card title={title}>
      {entries.length === 0 ? (
        <div className="muted">Sem dados.</div>
      ) : (
        entries.map(([label, value]) => (
          <Bar key={label} label={titleCase(label)} value={value} total={total} />
        ))
      )}
    </Card>
  );
}

export function DashboardPage() {
  const { data, error, loading, reload } = useApi<Summary>("/api/dashboard/summary", [], { poll: 5000 });

  if (loading && !data) return <div className="page"><Spinner /></div>;
  if (error) return <div className="page"><ErrorAlert message={error.message} /></div>;
  if (!data) return null;

  const { devices, requests, events, groups } = data;
  const online = devices.byStatus.online ?? 0;
  const darkPct = devices.total > 0 ? Math.round((devices.dark / devices.total) * 100) : 0;

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>Painel</h1>
          <p className="subtle">Visao consolidada da frota, requisicoes recentes e eventos.</p>
        </div>
        <span className="spacer" />
        <button className="btn" onClick={reload}>
          Atualizar
        </button>
      </div>

      <div className="grid cols-4">
        <Stat label="Dispositivos" value={devices.total} hint="inventario monitorado" />
        <Stat label="Online" value={online} hint={`${devices.total ? Math.round((online / devices.total) * 100) : 0}% da frota`} />
        <Stat label="Escuros" value={devices.dark} hint={`${darkPct}% offline ou ausentes`} />
        <Stat label="Risco >= 70" value={devices.riskAtLeast70} hint="requerem revisao" />
      </div>

      <div className="grid cols-3">
        <Bars title="Por status" data={devices.byStatus} />
        <Bars title="Por plataforma" data={devices.byPlatform} />
        <Bars title="Por criptografia" data={devices.byEncryption} />
      </div>

      <div className="grid cols-2">
        <Card title="Requisicoes recentes" action={<Link to="/requests">Ver fila</Link>} tight>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Requisicao</th>
                  <th>Dispositivo</th>
                  <th>Status</th>
                  <th>Criada</th>
                </tr>
              </thead>
              <tbody>
                {requests.recent.map((r) => (
                  <tr key={r.uid}>
                    <td>
                      <div className="cell-title">{titleCase(r.type)}</div>
                      <div className="cell-sub mono">{r.uid}</div>
                    </td>
                    <td>
                      <Link to={`/devices/${r.deviceUid}`}>{r.deviceName || r.deviceUid}</Link>
                    </td>
                    <td>
                      <Badge value={r.status} />
                    </td>
                    <td className="cell-sub">{relativeTime(r.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        <Card title="Eventos recentes">
          <div className="timeline">
            {events.map((e) => (
              <div className="timeline-item" key={e.id}>
                <span className="when">{formatDate(e.at).slice(0, 10)}</span>
                <span>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <Badge value={e.severity} />
                    <strong className="cell-sub mono">{e.type}</strong>
                  </div>
                  <div>{e.message}</div>
                  <div className="cell-sub mono">{e.device_uid}</div>
                </span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <Card title="Dispositivos por grupo" tight>
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Grupo</th>
                <th>Dispositivos</th>
                <th>Participacao</th>
              </tr>
            </thead>
            <tbody>
              {groups.map((g) => (
                <tr key={g.name}>
                  <td className="cell-title">{g.name}</td>
                  <td>{g.n}</td>
                  <td style={{ width: "55%" }}>
                    <span className="bar-track">
                      <span
                        className="bar-fill"
                        style={{ width: `${devices.total ? Math.round((g.n / devices.total) * 100) : 0}%` }}
                      />
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
